import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Popover, Box, Typography, List, ListItem, ListItemIcon, ListItemText,
  IconButton, Badge, Button,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  HourglassTop as PendingIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Circle as DotIcon,
  DoneAll as ReadAllIcon,
} from '@mui/icons-material';
import notificationsAPI from '../../api/notifications';
import { formatRelativeTime } from '../../utils/helpers';

const TYPE_ICON = {
  approval_pending: <PendingIcon sx={{ color: '#ed6c02' }} />,
  approval_approved: <ApproveIcon sx={{ color: '#2e7d32' }} />,
  approval_rejected: <RejectIcon sx={{ color: '#d32f2f' }} />,
};

const POLL_INTERVAL_MS = 60000;

const NotificationPanel = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const pollRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await notificationsAPI.getUnreadCount();
      setUnreadCount(res.data.count || 0);
    } catch { /* non-critical */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationsAPI.getAll();
      setNotifications(res.data.data || []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchUnreadCount]);

  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    fetchNotifications();
  };

  const markAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* non-critical */ }
  };

  const handleClickNotification = async (notif) => {
    if (!notif.read) {
      try {
        await notificationsAPI.markRead(notif._id);
        setNotifications(prev => prev.map(n => n._id === notif._id ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch { /* non-critical */ }
    }
    setAnchorEl(null);
    if (notif.link) navigate(notif.link);
  };

  const viewAll = () => {
    setAnchorEl(null);
    navigate('/approvals/pending');
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        sx={{
          width: 34, height: 34,
          color: 'text.disabled',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          sx={{ '& .MuiBadge-badge': { fontSize: '0.625rem', height: 16, minWidth: 16, fontWeight: 700 } }}
        >
          <NotificationsIcon sx={{ fontSize: 18 }} />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { width: 380, maxHeight: 480, mt: 1, border: '1px solid', borderColor: 'divider', boxShadow: '0 12px 40px rgba(0,0,0,0.12)' },
        }}
      >
        <Box sx={{ px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notifications</Typography>
            {unreadCount > 0 && (
              <Typography variant="caption" color="primary">{unreadCount} unread</Typography>
            )}
          </Box>
          {unreadCount > 0 && (
            <Button size="small" startIcon={<ReadAllIcon sx={{ fontSize: 16 }} />} onClick={markAllRead} sx={{ fontSize: '0.75rem' }}>
              Mark all read
            </Button>
          )}
        </Box>

        <List sx={{ py: 0, maxHeight: 380, overflowY: 'auto' }}>
          {notifications.length === 0 && (
            <Box sx={{ px: 2.5, py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No notifications yet</Typography>
            </Box>
          )}
          {notifications.map((notif) => (
            <ListItem
              key={notif._id}
              onClick={() => handleClickNotification(notif)}
              sx={{
                px: 2.5, py: 1.5,
                cursor: 'pointer',
                bgcolor: notif.read ? 'transparent' : 'action.hover',
                borderLeft: notif.read ? '3px solid transparent' : '3px solid',
                borderLeftColor: notif.read ? 'transparent' : 'primary.main',
                '&:hover': { bgcolor: 'action.selected' },
                transition: 'all 150ms',
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {TYPE_ICON[notif.type] || <NotificationsIcon sx={{ color: 'text.secondary' }} />}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: notif.read ? 400 : 600, fontSize: '0.813rem' }}>
                      {notif.title}
                    </Typography>
                    {!notif.read && <DotIcon sx={{ fontSize: 6, color: 'primary.main' }} />}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                      {notif.message}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.625rem' }}>
                      {formatRelativeTime(notif.createdAt)}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>

        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Button size="small" sx={{ fontSize: '0.75rem' }} onClick={viewAll}>View All Notifications</Button>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationPanel;
